import { useCallback } from 'react';
import { SimulationConfig, Ball, Vector2D, ShapeType, EscapeRing, TrailCircle, CollisionEvent, BlastEvent, SpaceSpiralRoundConfig, BallPiece, BallPlacementConfig, RhythmicSimulationState } from '@/types';
import { GPURenderer } from '@/render/GPURenderer';
import { TrailCirclePool } from '@/services/TrailCirclePool';
import { CollisionEventPool } from '@/services/CollisionEventPool';
import { BlastEventPool } from '@/services/BlastEventPool';
import { AUDIO } from '@/services/AudioService';
import { CAMERA } from '@/services/CameraService';
import { getRandomColor } from '@/lib/colorUtils';
import { BallPool } from '@/services/BallPool';
import { CoreSimulationLogic } from '@/logic/CoreSimulationLogic';
import { PRESET_LIBRARY } from '@/data/presets';
import { setGlobalSeed, random } from '@/lib/random';

export const useSimulationInit = (
  configRef: React.MutableRefObject<SimulationConfig>,
  ballsRef: React.MutableRefObject<Ball[]>,
  trailCirclesRef: React.MutableRefObject<TrailCircle[]>,
  collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
  blastEventsRef: React.MutableRefObject<BlastEvent[]>,
  escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
  initialEscapeRingCountRef: React.MutableRefObject<number>,
  timeScaleRef: React.MutableRefObject<number>,
  isRotationStoppedRef: React.MutableRefObject<boolean>,
  shakeRef: React.MutableRefObject<number>,
  counterPopRef: React.MutableRefObject<number>,
  timeRemainingRef: React.MutableRefObject<number>,
  containerRadiusRef: React.MutableRefObject<number>,
  rhythmicIndexRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
  morphTimerRef: React.MutableRefObject<number>,
  morphStateRef: React.MutableRefObject<{ active: boolean, from: ShapeType, to: ShapeType, progress: number }>,
  escapeAccelerationBoostRef: React.MutableRefObject<number>,
  currentRoundRef: React.MutableRefObject<number>,
  roundStartTimeRef: React.MutableRefObject<number>,
  roundOverridesRef: React.MutableRefObject<SpaceSpiralRoundConfig | null>,
  ballPoolRef: React.MutableRefObject<BallPool>,
  trailCirclePoolRef: React.MutableRefObject<TrailCirclePool>,
  collisionEventPoolRef: React.MutableRefObject<CollisionEventPool>,
  blastEventPoolRef: React.MutableRefObject<BlastEventPool>,
  speedIncreaseRateRef: React.MutableRefObject<number>,
  ballGrowthRateRef: React.MutableRefObject<number>,
  containerShrinkRateRef: React.MutableRefObject<number>,
  rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
  persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
  rhythmicResetTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  rhythmicSimulationsStateRef: React.MutableRefObject<RhythmicSimulationState[]>,
  simTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldActiveRef: React.MutableRefObject<boolean>,
  rhythmicEscapeShieldBouncesRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldAngleRef: React.MutableRefObject<number>,
  resetWorker: () => void,
  sendBallsToWorker: (balls: Ball[], now?: number) => void,
  setBallCount: React.Dispatch<React.SetStateAction<number>>,
  setBounceCount: React.Dispatch<React.SetStateAction<number>>,
  setElapsedTime: React.Dispatch<React.SetStateAction<number>>,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>,
  lastTimeRef: React.MutableRefObject<number>,
  webglRendererRef?: React.MutableRefObject<GPURenderer | null>
) => {
  const getInitialBallState = useCallback((index: number, total: number, config: BallPlacementConfig | null = null, isForPreview: boolean = false, radiusOverride?: number) => {
    const cfg = configRef.current;
    const mode = config ? config.mode : cfg.ballPlacementMode;
    const speed = config ? config.speed : (cfg.initialSpeed * 12);
    const randomDir = config ? config.randomDirection : cfg.ballPlacementRandomDirection;
    const dirAngle = config ? config.directionAngle : cfg.ballPlacementDirectionAngle;
    const containerRadius = radiusOverride || cfg.containerRadius;

    // The simulation center is (0,0). The renderer handles translation to canvas center.
    const centerX = 0;
    const centerY = 0;

    let pos = { x: centerX, y: centerY };
    
    if (mode === 'MANUAL') {
        // posX and posY are in range [-100, 100] where 0 is center
        const px = config ? config.posX : cfg.ballPlacementPosX;
        const py = config ? config.posY : cfg.ballPlacementPosY;
        const limit = containerRadius - cfg.ballRadius - 5;
        pos = { 
            x: centerX + (px / 100) * limit, 
            y: centerY + (py / 100) * limit 
        };
    } else if (mode === 'BORDER') {
        const border = config ? config.border : cfg.ballPlacementBorder;
        const offset = containerRadius - cfg.ballRadius - 5;
        if (border === 'TOP') pos = { x: centerX, y: centerY - offset };
        else if (border === 'BOTTOM') pos = { x: centerX, y: centerY + offset };
        else if (border === 'LEFT') pos = { x: centerX - offset, y: centerY };
        else if (border === 'RIGHT') pos = { x: centerX + offset, y: centerY };
    } else if (mode === 'ANGLE') {
        const angle = (config ? config.placementAngle : cfg.ballPlacementAngle) * Math.PI / 180;
        const dist = (config ? config.distance : cfg.ballPlacementDistance) / 100 * containerRadius;
        pos = { 
            x: centerX + Math.cos(angle) * dist, 
            y: centerY + Math.sin(angle) * dist 
        };
    }

    // Clamp to container
    const dx = pos.x - centerX;
    const dy = pos.y - centerY;
    const dist = Math.sqrt(dx*dx + dy*dy);
    const maxDist = containerRadius - cfg.ballRadius - 5;
    if (dist > maxDist) {
        const scale = maxDist / dist;
        pos.x = centerX + dx * scale;
        pos.y = centerY + dy * scale;
    }

    let vel;
    if (randomDir && !isForPreview) {
        const angle = random.range(0, Math.PI * 2);
        vel = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    } else {
        const angle = dirAngle * Math.PI / 180;
        vel = { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed };
    }

    return { pos, vel };
  }, [configRef]);

  const initSimulation = useCallback((resetRounds: boolean = false) => {
    const cfg = configRef.current;
    if (cfg.rngSeed) {
        setGlobalSeed(cfg.rngSeed);
    }
    const now = Date.now(); // We still use actual Date.now() here as the reference start, but it will be fixed for cloud rendering later if needed
    
    // Use a local effective config for round overrides to avoid corrupting the global state
    let effectiveCfg = { ...cfg };

    // Enforcement: Non-spawner modes are limited to 1 ball
    if (effectiveCfg.mode !== 'SPAWNER') {
        effectiveCfg.initialBalls = 1;
        effectiveCfg.maxBalls = 1;
    }

    // Playlist Sequential Reset: Start from the first song if in playlist mode
    if (cfg.mode === 'RHYTHMIC' && cfg.playlistEnabled && cfg.playlist.length > 0) {
        const firstItem = cfg.playlist[0];
        let newInitParams: any = {};
        
        if (cfg.playlistCurrentIndex !== 0) newInitParams.playlistCurrentIndex = 0;
        if (cfg.selectedMelody !== firstItem.melodyId) newInitParams.selectedMelody = firstItem.melodyId;

        if (firstItem.presetName && firstItem.presetName !== 'Default') {
            const preset = PRESET_LIBRARY.find(p => p.name === firstItem.presetName);
            if (preset) {
                // Check each preset setting for changes
                Object.entries(preset.settings).forEach(([key, val]) => {
                    if ((cfg as any)[key] !== val) newInitParams[key] = val;
                });
            }
        }
        
        if (Object.keys(newInitParams).length > 0) {
            effectiveCfg = { ...effectiveCfg, ...newInitParams };
            setConfig(prev => ({ ...prev, ...newInitParams }));
        }
    }
    
    ballsRef.current.forEach(b => ballPoolRef.current.release(b));
    ballsRef.current = [];
    resetWorker();

    // Reset Shape to Initial
    if (cfg.initialContainerShape && cfg.containerShape !== cfg.initialContainerShape) {
        // Use a functional update to avoid unnecessary re-renders if the value is already correct
        setConfig(prev => {
            if (prev.containerShape === cfg.initialContainerShape) return prev;
            return { ...prev, containerShape: cfg.initialContainerShape };
        });
    }

    // Legacy Mode & Combined Feature Mapping
    if (effectiveCfg.mode === 'RHYTHMIC' || effectiveCfg.mode === 'RHYTHMIC_ESCAPE') effectiveCfg.enableRhythmic = true;
    if (effectiveCfg.mode === 'ESCAPE' || effectiveCfg.mode === 'RHYTHMIC_ESCAPE') effectiveCfg.enableEscape = true;
    if (effectiveCfg.mode === 'TRAIL') effectiveCfg.enableTrails = true;
    if (effectiveCfg.mode === 'DRAWER') effectiveCfg.enableDrawer = true;
    if (effectiveCfg.mode === 'SPAWNER') effectiveCfg.enableSpawner = true;
    if (effectiveCfg.mode === 'SPACE_SPIRAL') effectiveCfg.enableSpaceSpiral = true;

    AUDIO.resetIndex();
    AUDIO.resetSongCursor(); 
    CAMERA.reset();
    setBounceCount(0);
    
    trailCirclesRef.current.forEach(c => trailCirclePoolRef.current.release(c));
    trailCirclesRef.current = [];
    
    timeScaleRef.current = cfg.masterSpeed || 1.0;
    isRotationStoppedRef.current = false;
    shakeRef.current = 0;
    counterPopRef.current = 1.0;
    containerRadiusRef.current = cfg.containerRadius;
    rhythmicTimeScaleRef.current = cfg.rhythmicTimeScale;
    rhythmicTimeScaleRateRef.current = cfg.rhythmicTimeScaleRate;
    speedIncreaseRateRef.current = cfg.speedIncreaseRate;
    ballGrowthRateRef.current = cfg.ballGrowthRate;
    containerShrinkRateRef.current = cfg.containerShrinkRate;
    simTimeRef.current = 0;
    morphTimerRef.current = 0;
    morphStateRef.current = { active: false, from: configRef.current.containerShape, to: configRef.current.containerShape, progress: 0 };
    
    let ballIdCounter = 0;
    const createBall = (overrides: Partial<Ball>): Ball => {
        const b = ballPoolRef.current.get(
            overrides.id || `ball-${ballIdCounter++}`,
            overrides.pos || { x: 0, y: 0 },
            overrides.vel || { x: random.range(-5, 5), y: random.range(-5, 5) },
            overrides.radius || effectiveCfg.ballRadius,
            overrides.color || effectiveCfg.ballColor,
            overrides.mass || effectiveCfg.ballMass,
            overrides.style || effectiveCfg.ballStyle,
            overrides.isBox || false
        );
        if (overrides.rotationSpeed !== undefined) b.rotationSpeed = overrides.rotationSpeed;
        if (overrides.shape) b.shape = overrides.shape;
        if (overrides.dimensions) b.dimensions = overrides.dimensions;
        if (overrides.originalPos) b.originalPos = overrides.originalPos;
        if (overrides.angle !== undefined) b.angle = overrides.angle;
        if (overrides.isBox !== undefined) b.isBox = overrides.isBox;
        if (overrides.spawnTime !== undefined) b.spawnTime = overrides.spawnTime;
        return b;
    };

    initialEscapeRingCountRef.current = effectiveCfg.escapeRingCount || 10;
    
    // Sequential Initializers
    if (effectiveCfg.enableEscape) {
        ballsRef.current = CoreSimulationLogic.EscapeLogic.init(effectiveCfg, 0, 0, escapeRingsRef, initialEscapeRingCountRef, timeRemainingRef, escapeAccelerationBoostRef, createBall, getInitialBallState, now);
        // Reset shield for Rhythmic Escape
        rhythmicEscapeShieldActiveRef.current = !!effectiveCfg.rhythmicEscapeShieldRing;
        rhythmicEscapeShieldBouncesRef.current = 0;
        rhythmicEscapeShieldTimeRef.current = 0;
        rhythmicEscapeShieldAngleRef.current = 0;
    }

    if (effectiveCfg.enableRhythmic) {
        if (cfg.rhythmicMultiSimEnabled) {
            // ... already complex, keep for now but use effectiveCfg
            const sims = cfg.rhythmicSimulations;
            const count = sims.length;
            const spacing = window.innerWidth / (count + 1);
            
            rhythmicSimulationsStateRef.current = sims.map((sim, i) => {
                const simSettings = sim.settings || {};
                const simCenter = cfg.rhythmicMultiSimIntroMode ? { x: 0, y: 0 } : (simSettings.rhythmicSimCenter || { x: spacing * (i + 1) - window.innerWidth / 2, y: 0 });
                const simRadius = simSettings.containerRadius || cfg.containerRadius;
                
                const state: RhythmicSimulationState = {
                    id: sim.id,
                    ball: null,
                    containerRadius: simRadius,
                    rhythmicIndex: 0,
                    ballGrowthRate: simSettings.ballGrowthRate ?? cfg.ballGrowthRate,
                    rhythmicTimeScale: simSettings.rhythmicTimeScale ?? cfg.rhythmicTimeScale,
                    rhythmicTimeScaleRate: simSettings.rhythmicTimeScaleRate ?? cfg.rhythmicTimeScaleRate,
                    rhythmicTimeScaleAcceleration: simSettings.rhythmicTimeScaleAcceleration ?? cfg.rhythmicTimeScaleAcceleration,
                    center: { ...simCenter },
                    targetCenter: { ...simCenter },
                    active: !cfg.rhythmicMultiSimIntroMode,
                    spawnTime: Date.now(),
                    trackIndices: sim.trackIndices,
                    backgroundTrackIndices: sim.backgroundTrackIndices || [],
                    mutedTrackIndices: sim.mutedTrackIndices || [],
                    settings: sim.settings
                };

                if (state.active) {
                    const localIndexRef = { current: 0 };
                    const mergedCfg = { ...effectiveCfg, ...simSettings };
                    state.ball = CoreSimulationLogic.RhythmicLogic.init(mergedCfg, localIndexRef as any, createBall, simCenter.x, simCenter.y, (idx, tot, c) => getInitialBallState(idx, tot, c, false, mergedCfg.containerRadius), now)[0];
                    state.rhythmicIndex = localIndexRef.current;
                }

                return state;
            });
            ballsRef.current = rhythmicSimulationsStateRef.current.map(s => s.ball).filter(b => b !== null) as Ball[];
        } else {
            // Only overwrite balls if not already initialized by Escape
            const rhythmicBalls = CoreSimulationLogic.RhythmicLogic.init(effectiveCfg, rhythmicIndexRef, createBall, 0, 0, getInitialBallState, now);
            if (ballsRef.current.length === 0) {
                ballsRef.current = rhythmicBalls;
            } else if (rhythmicBalls.length > 0) {
                // If we have both, we take the Rhythmic properties and apply them to the first Escape ball
                const rb = rhythmicBalls[0];
                const eb = ballsRef.current[0];
                eb.spawnTime = rb.spawnTime;
                eb.curvature = rb.curvature;
                // Release the extra rhythmic ball
                ballPoolRef.current.release(rb);
            }
        }
    }

    // Default fallbacks if no features enabled
    if (ballsRef.current.length === 0) {
        if (effectiveCfg.enableSpaceSpiral) {
            ballsRef.current = CoreSimulationLogic.SpaceSpiralLogic.init(effectiveCfg, 0, 0, currentRoundRef, roundStartTimeRef, roundOverridesRef, resetRounds, createBall, getInitialBallState, now);
        } else {
            ballsRef.current = CoreSimulationLogic.BasicLogic.init(effectiveCfg, createBall, getInitialBallState, now);
        }
    }
    setBallCount(ballsRef.current.length);
    setElapsedTime(0);
    lastTimeRef.current = now;
    
    // Recycle active events before clearing
    collisionEventsRef.current.forEach(e => {
        if (collisionEventPoolRef && collisionEventPoolRef.current) {
            collisionEventPoolRef.current.release(e);
        }
    });
    collisionEventsRef.current = [];

    blastEventsRef.current.forEach(e => {
        if (blastEventPoolRef && blastEventPoolRef.current) {
            blastEventPoolRef.current.release(e);
        }
    });
    blastEventsRef.current = [];
    
    rhythmicBreakPiecesRef.current = [];
    if (persistentSpectrumBoostRef.current) {
        persistentSpectrumBoostRef.current.fill(0);
    }
    if (rhythmicResetTimeoutRef.current) {
        clearTimeout(rhythmicResetTimeoutRef.current);
        rhythmicResetTimeoutRef.current = null;
    }
    if (webglRendererRef && webglRendererRef.current) {
        webglRendererRef.current.resetGPUPasses();
    }
    sendBallsToWorker(ballsRef.current, now);
  }, [configRef, resetWorker, sendBallsToWorker, getInitialBallState, setBallCount, setBounceCount, setElapsedTime, lastTimeRef, webglRendererRef]);

  const getBallPreviews = useCallback(() => {
    const cfg = configRef.current;
    const previews: { pos: {x:number, y:number}, vel: {x:number, y:number}, radius: number, color: string }[] = [];
    const count = cfg.initialBalls;
    for (let i = 0; i < count; i++) {
        const individualCfg = cfg.ballPlacementIndividualEnabled && cfg.ballPlacementConfigs[i] ? cfg.ballPlacementConfigs[i] : null;
        const { pos, vel } = getInitialBallState(i, count, individualCfg, true);
        previews.push({
            pos,
            vel,
            radius: cfg.ballRadius,
            color: individualCfg?.color || (i === 0 ? cfg.ballColor : (i === 1 ? cfg.ball2Color : '#ffffff'))
        });
    }
    return previews;
  }, [configRef, getInitialBallState]);

  return { initSimulation, getBallPreviews };
};
